import React, { useCallback, useEffect, useRef, useState } from "react";
import { CLUSTER_TONE, useTheme } from "../lib/theme";

const rgba = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

export function ArtifactCluster({ nodes = [], height = 360 }) {
  const canvasRef = useRef(null);
  const [hover, setHover] = useState(null);
  const { viz } = useTheme();
  const groupColor = useCallback((g) => (viz[CLUSTER_TONE[g]] || viz.cyan)[0], [viz]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || nodes.length === 0) return undefined;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    let w = 0;
    let h = 0;
    let raf = 0;
    let tick = 0;

    const groups = [...new Set(nodes.map((n) => n.group))];
    const centers = {};
    const parts = nodes.map((n) => ({
      ...n,
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      r: 3.2 + Math.min(7, (n.score || 0) / 16),
      ph: Math.random() * Math.PI * 2,
    }));

    const layout = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      groups.forEach((g, i) => {
        const a = (i / groups.length) * Math.PI * 2 - Math.PI / 2;
        const rad = Math.min(w, h) * (groups.length === 1 ? 0 : 0.25);
        centers[g] = { x: w / 2 + Math.cos(a) * rad, y: h / 2 + Math.sin(a) * rad * 0.74 };
      });
      parts.forEach((p) => {
        if (p.x === 0 && p.y === 0) {
          const c = centers[p.group];
          p.x = c.x + (Math.random() - 0.5) * 80;
          p.y = c.y + (Math.random() - 0.5) * 80;
        }
      });
    };
    layout();

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      let best = null;
      let bd = 24;
      for (const p of parts) {
        const d = Math.hypot(p.x - mx, p.y - my);
        if (d < bd) {
          bd = d;
          best = p;
        }
      }
      setHover(best ? { label: best.label, group: best.group, score: best.score, meta: best.meta, x: best.x, y: best.y } : null);
    };
    const onLeave = () => setHover(null);
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);
    const ro = new ResizeObserver(layout);
    ro.observe(canvas);

    const frame = () => {
      tick += 0.012;
      ctx.clearRect(0, 0, w, h);

      groups.forEach((g) => {
        const c = centers[g];
        const col = groupColor(g);
        const grd = ctx.createRadialGradient(c.x, c.y, 4, c.x, c.y, 96);
        grd.addColorStop(0, rgba(col, 0.13));
        grd.addColorStop(1, rgba(col, 0));
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(c.x, c.y, 96, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = rgba(col, 0.16);
        ctx.setLineDash([3, 6]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(c.x, c.y, 52 + Math.sin(tick * 1.6) * 3, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = rgba(col, 0.68);
        ctx.font = "500 9.5px 'JetBrains Mono', monospace";
        ctx.textAlign = "center";
        ctx.fillText(g, c.x, Math.max(12, c.y - 64));
      });

      for (const p of parts) {
        const c = centers[p.group];
        p.vx += (c.x - p.x) * 0.0026;
        p.vy += (c.y - p.y) * 0.0026;
        for (const q of parts) {
          if (q === p) continue;
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 1100 && d2 > 0.5) {
            const f = 34 / d2;
            p.vx += dx * f * 0.05;
            p.vy += dy * f * 0.05;
          }
        }
        p.vx += Math.cos(tick + p.ph) * 0.014;
        p.vy += Math.sin(tick * 1.3 + p.ph) * 0.014;
        p.vx *= 0.9;
        p.vy *= 0.9;
        p.x += p.vx;
        p.y += p.vy;
        p.x = Math.max(14, Math.min(w - 14, p.x));
        p.y = Math.max(24, Math.min(h - 14, p.y));
      }

      ctx.lineWidth = 1;
      for (let i = 0; i < parts.length; i++) {
        for (let j = i + 1; j < parts.length; j++) {
          const a = parts[i];
          const b = parts[j];
          if (a.group !== b.group) continue;
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d > 118) continue;
          ctx.strokeStyle = rgba(groupColor(a.group), 0.2 * (1 - d / 118));
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      for (const p of parts) {
        const col = groupColor(p.group);
        const pulse = 0.55 + 0.45 * Math.sin(tick * 2 + p.ph);
        const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 4.2);
        grd.addColorStop(0, rgba(col, 0.5 * pulse));
        grd.addColorStop(1, rgba(col, 0));
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 4.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = col;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = viz.clusterStroke;
        ctx.lineWidth = 0.7;
        ctx.stroke();
      }
      raf = requestAnimationFrame(frame);
    };
    frame();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
    };
  }, [nodes, viz, groupColor]);

  return (
    <div className="relative overflow-hidden rounded-lg border border-edge well" style={{ height }}>
      <canvas ref={canvasRef} className="h-full w-full" data-testid="artifact-cluster-canvas" />
      {hover && (
        <div
          className="pointer-events-none absolute z-10 max-w-[240px] rounded-md border border-edgeStrong bg-void/95 px-2.5 py-1.5"
          style={{ left: Math.min(hover.x + 14, 9999), top: Math.max(hover.y - 18, 4) }}
          data-testid="cluster-tooltip"
        >
          <p className="font-mono text-[10px]" style={{ color: groupColor(hover.group) }}>
            {hover.group}
          </p>
          <p className="text-[11px] text-slate-100">{hover.label}</p>
          {hover.meta && <p className="font-mono text-[10px] text-slate-400">{hover.meta}</p>}
        </div>
      )}
    </div>
  );
}
